import { useUser, useWorkTask } from "@tm/context-distribution"
import { Toolbar } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { ECounterType, RegistrationNoType } from "@tm/models"
import { RouteComponentProps, TmaHelper, withRouter } from "@tm/utils"
import { Box, Stack, styled } from "@tm/components"
import { useShowVehicleLookupConfig } from "@bundles/vehicle/data/hooks/useShowVehicleLookupConfig"
import { useVehicleUrlParameter } from "@bundles/vehicle/hooks/useVehicleUrlParamter"
import { useSelectedSimpleLookupConfig } from "@bundles/vehicle/data/hooks/useSelectedSimpleLookupConfig"
import { getBundleParams } from "../../utils"
import { getLookupType } from "../../helpers"
import { getVehicleSearchUrl } from "../../helpers/routing"
import { useResetFilters } from "../../data/hooks"
import { VehicleSearchFieldSwitch } from "../_shared/VehicleSearchFieldSwitch"
import { SearchSelection } from "../_shared/VehicleSearchFieldV2/SearchSelection"

type Props = RouteComponentProps<{ vehicleType: string } & SearchRouteProps & SelectRouteParams>

type SearchRouteProps = {
    query: string
    lookUpTypeId: string
}

type SelectRouteParams = {
    view: string
    workTaskId: string
}

const StyledVehicleSearchFieldSwitch = styled(VehicleSearchFieldSwitch)({
    minWidth: "500px",
})

function NavigationComponent(props: Props) {
    const vehicleUrlParameter = useVehicleUrlParameter()

    const localization = useLocalization()
    const workTask = useWorkTask()
    const { userContext } = useUser() ?? {}
    const resetFilters = useResetFilters(vehicleUrlParameter.vehicleType)

    const { simpleVehicleSearch } = getBundleParams()
    const { data, isFetching } = useShowVehicleLookupConfig(simpleVehicleSearch)

    const { selectedConfig } = useSelectedSimpleLookupConfig()

    const startSearch = (query: string, regNoType?: RegistrationNoType) => {
        // Special for Create Business: replace dashes in query (see: NEXT-9621) 2 of 2
        if (getLookupType(userContext) === RegistrationNoType.KennzeichenPortugal && vehicleUrlParameter.checkAndMergeRegNoDetails) {
            query = query?.replace(/-/g, "")
        }

        TmaHelper.GeneralCountEvent.Call(ECounterType.VehicleSelectionSearchSymbol)

        resetFilters() // Reset filters before opening search page
        props.history.push(
            getVehicleSearchUrl(props.match.params, {
                vehicleType: vehicleUrlParameter.vehicleType,
                query,
                forceUpdate: true,
                registrationNoType: regNoType,
                checkAndMergeRegNoDetails: vehicleUrlParameter.checkAndMergeRegNoDetails,
            })
        )
    }

    const handleAttachVehicleId = (vehicleId: string, query: string) => {
        TmaHelper.VehicleSelection.Search.FromWidget({ dataSourceId: RegistrationNoType.VehicleBase, query })
        workTask?.attachToWorkTask({ vehicle: vehicleId })
    }

    let initialSearchQuery
    let initialLicensePlateQuery

    if (vehicleUrlParameter.query) {
        initialSearchQuery = decodeURIComponent(vehicleUrlParameter.query)
    }

    if (vehicleUrlParameter.licensePlateSearchQuery) {
        initialLicensePlateQuery = decodeURIComponent(vehicleUrlParameter.licensePlateSearchQuery)
    }

    let initialLookupId
    if (vehicleUrlParameter.regNoType !== undefined) {
        initialLookupId = vehicleUrlParameter.regNoType
    }

    if (!initialLookupId && selectedConfig) {
        initialLookupId = selectedConfig.lookupId
    }

    if (getBundleParams().nextLight) {
        return null
    }

    return (
        <Toolbar title={localization.translateText(135)} className="tk-vehicle searchfield">
            <Stack direction="row" spacing={1}>
                {simpleVehicleSearch && (
                    <Box>
                        <SearchSelection lookupConfigs={data?.lookupConfigs} loading={isFetching} initialLookupId={initialLookupId} />
                    </Box>
                )}
                <StyledVehicleSearchFieldSwitch
                    {...props}
                    initialLicensePlateQuery={initialLicensePlateQuery}
                    initialQuery={initialSearchQuery}
                    onStartSearch={startSearch}
                    onAttachVehicleId={handleAttachVehicleId}
                    vehicleType={vehicleUrlParameter.vehicleType}
                    showVrcScanButton={!workTask?.workTask?.vehicle}
                    disableEdsSearch
                    hideTooltip
                    direction="row"
                    hideSpecialLicensePlateSearchField={!simpleVehicleSearch}
                />
            </Stack>
        </Toolbar>
    )
}

export default withRouter(NavigationComponent)
